import { Employee, TeamData, BudgetData, TeamPerformanceData } from '../contexts/AppContext.tsx';
import { ActualSalesService, ActualSalesResult } from './actualSalesService.ts';
import { TeamDataUtils } from '../utils/teamDataUtils.ts';
import { logger } from '../utils/logger.ts';
import { resolveTeamIncentiveRatio } from '../utils/payrollCalculations.ts';

import { saveBudgetMonthReplace } from './firebase/budgetFirebaseService.ts';

// 팀 성과 데이터 계산 서비스
export class TeamPerformanceService {
  
  /**
   * 팀 성과 데이터를 계산합니다
   * @param yearMonth 계산할 년월 (YYYY-MM 형식)
   * @param teams 팀 데이터
   * @param employees 직원 데이터
   * @param budgetData 예산 데이터
   * @returns 계산된 팀 성과 데이터
   */
  static async calculateTeamPerformance(
    yearMonth: string,
    teams: TeamData[],
    employees: Employee[],
    budgetData: BudgetData,
    options?: { forceRefresh?: boolean }
  ): Promise<{
    teamPerformanceData: Record<string, TeamPerformanceData>;
    totalActualSales: number;
    teamTargetSales: Record<string, number>;
    totalWorkingDays: number;  // 🆕 전체 근무일
    actualSalesResult: ActualSalesResult;  // 🔧 FIX: refreshService에서 캐시 병합용
  }> {
    logger.debug(`[TeamPerformanceService] ${yearMonth} 팀 성과 계산 시작`);

    // ✅ 월별 저장 직원 데이터 우선 사용 (급여 관리와 동일)
    const monthlyEmployees = budgetData.monthlyData?.[yearMonth]?.employees;
    const targetEmployees = (monthlyEmployees && monthlyEmployees.length > 0)
      ? monthlyEmployees
      : employees;

    if (targetEmployees !== employees) {
      logger.debug(`[TeamPerformanceService] ${yearMonth} 월별 저장 직원 데이터 사용`, { context: { count: targetEmployees.length } });
    } else {
      logger.debug(`[TeamPerformanceService] ${yearMonth} 전역 직원 데이터 사용`, { context: { count: targetEmployees.length } });
    }

    // 🔧 리팩토링: TeamDataUtils 사용하여 복합 팀 데이터 생성
    const validTeamNames = TeamDataUtils.generateCompositeTeamData(
      teams,
      targetEmployees,
      budgetData,
      yearMonth
    );

    // 🔧 리팩토링: TeamDataUtils 사용하여 팀별 목표매출 계산
    const teamTargetSales = TeamDataUtils.calculateTeamTargetSales(
      validTeamNames,
      targetEmployees,
      budgetData,
      yearMonth
    );

    logger.debug(`[TeamPerformanceService] ${yearMonth} 최종 팀별 목표매출`, { context: { teamTargetSales } });
    
    // 2. 실제매출 데이터 가져오기 (ActualSalesService 활용)
    let actualSalesResult: ActualSalesResult;
    
    // 캐시된 데이터 먼저 확인 (forceRefresh 시 캐시 스킵)
    const cachedActualSales = options?.forceRefresh
      ? null
      : await ActualSalesService.loadCachedActualSales(yearMonth, budgetData);

    if (cachedActualSales) {
      logger.debug(`[TeamPerformanceService] ${yearMonth} 캐시된 실제매출 데이터 사용`);
      actualSalesResult = cachedActualSales;
    } else {
      if (options?.forceRefresh) {
        logger.debug(`[TeamPerformanceService] ${yearMonth} forceRefresh — 캐시 스킵, 실시간 계산`);
      } else {
        logger.debug(`[TeamPerformanceService] ${yearMonth} 실제매출 실시간 계산`);
      }
      actualSalesResult = await ActualSalesService.calculateMonthlyActualSales(yearMonth, targetEmployees, budgetData);

      // 계산 후 자동 저장
      try {
        await ActualSalesService.saveActualSalesData(yearMonth, actualSalesResult, budgetData);
        logger.debug(`[TeamPerformanceService] ${yearMonth} 실제매출 데이터 자동 저장 완료`);
      } catch (saveError) {
        logger.error(`[TeamPerformanceService] ${yearMonth} 실제매출 데이터 저장 실패`, saveError instanceof Error ? saveError : undefined);
      }
    }
    
    const teamActualSales = actualSalesResult.teamActualSales;
    const totalActualSales = actualSalesResult.totalActualSales;
    const workingDaysStats = actualSalesResult.workingDaysStats;  // 🆕 근무일 통계

    logger.debug(`[TeamPerformanceService] ${yearMonth} 팀별 실제매출`, { context: { teamActualSales } });
    logger.debug(`[TeamPerformanceService] ${yearMonth} 근무일 통계`, {
      context: {
        totalWorkingDays: workingDaysStats?.totalWorkingDays,
        teamWorkingDays: workingDaysStats?.teamWorkingDays
      }
    });
    
    // 3. 팀별 성과 데이터 계산
    const teamPerformanceData: Record<string, TeamPerformanceData> = {};
    
    // 먼저 초과/미달성 매출 계산 (목표매출이 있는 팀만)
    const exceededTeams: { teamName: string; exceededSales: number }[] = [];
    const underperformingTeams: { teamName: string; underperformanceSales: number }[] = [];
    
    validTeamNames.forEach(teamName => {
      const targetSales = teamTargetSales[teamName] || 0;
      const actualSales = teamActualSales[teamName] || 0;
      const achievementRate = targetSales > 0 ? actualSales / targetSales : 0;
      
      if (achievementRate > 1) {
        // 목표 초과 달성
        exceededTeams.push({
          teamName,
          exceededSales: actualSales - targetSales
        });
      } else if (achievementRate < 1 && targetSales > 0) {
        // 목표 미달성
        underperformingTeams.push({
          teamName,
          underperformanceSales: targetSales - actualSales
        });
      }
    });
    
    // 총 미달성 매출 및 차감분 계산
    const totalUnderperformanceSales = underperformingTeams.reduce(
      (sum, team) => sum + team.underperformanceSales, 0
    );
    const totalDeductionAmount = totalUnderperformanceSales * ((budgetData.underperformanceDeductionRate || 0) / 100);
    
    // 초과 달성 팀들 간의 차감분 분배 (초과매출 비율에 따라)
    const totalExceededSales = exceededTeams.reduce((sum, team) => sum + team.exceededSales, 0);
    
    // 🔧 중요: 모든 팀에 대해 성과 데이터 생성 (목표매출이 0이어도)
    validTeamNames.forEach(teamName => {
      const targetSales = teamTargetSales[teamName] || 0;
      const actualSales = teamActualSales[teamName] || 0;
      const achievementRate = targetSales > 0 ? actualSales / targetSales : 0;
      
      const exceededSales = Math.max(0, actualSales - targetSales);
      const underperformanceSales = Math.max(0, targetSales - actualSales);
      
      // 차감분 계산 (초과 달성 팀만 차감, 목표매출 0원 팀 제외)
      let deductionAmount = 0;
      if (exceededSales > 0 && totalExceededSales > 0 && targetSales > 0) {
        const deductionRatio = exceededSales / totalExceededSales;
        deductionAmount = Math.round(totalDeductionAmount * deductionRatio);
      }
      
      // 인센티브 산출 매출 = 초과매출 - 차감분
      const incentiveCalculationSales = Math.max(0, exceededSales - deductionAmount);
      
      // 인센티브 계산 (월별 우선 → 전역 동결 폴백)
      const teamRatio = resolveTeamIncentiveRatio(budgetData, yearMonth, teamName);

      const incentiveAmount = Math.round(incentiveCalculationSales * (teamRatio.performance / 100));
      
      // 🆕 근무일 및 일평균 매출 계산
      const teamWorkingDays = workingDaysStats?.teamWorkingDays?.[teamName] || 0;
      const dailyAverageSales = teamWorkingDays > 0
        ? Math.round(actualSales / teamWorkingDays)
        : 0;

      teamPerformanceData[teamName] = {
        teamName,
        targetSales,
        actualSales,
        achievementRate,
        incentiveAmount,
        exceededSales,
        underperformanceSales,
        deductionAmount,
        incentiveCalculationSales,
        // 🆕 근무일/일평균 통계
        workingDays: teamWorkingDays,
        dailyAverageSales
      };
      
      logger.debug(`[TeamPerformanceService] ${teamName} 성과 데이터 생성`, {
        context: {
          targetSales: targetSales.toLocaleString(),
          actualSales: actualSales.toLocaleString(),
          achievementRate: (achievementRate * 100).toFixed(1) + '%',
          incentiveAmount: incentiveAmount.toLocaleString()
        }
      });
    });

    logger.debug(`[TeamPerformanceService] ${yearMonth} 팀 성과 계산 완료`, {
      context: {
        teamCount: validTeamNames.length,
        totalActualSales: totalActualSales.toLocaleString(),
        totalUnderperformanceSales: totalUnderperformanceSales.toLocaleString(),
        totalDeductionAmount: totalDeductionAmount.toLocaleString()
      }
    });
    
    return {
      teamPerformanceData,
      totalActualSales,
      teamTargetSales,
      totalWorkingDays: workingDaysStats?.totalWorkingDays || 0,  // 🆕 전체 근무일
      actualSalesResult  // 🔧 FIX: refreshService에서 캐시 병합용
    };
  }
  
  /**
   * 월별 팀 성과 데이터를 로드합니다 (캐시 우선)
   * @param yearMonth 로드할 년월
   * @param budgetData 현재 예산 데이터
   * @returns 저장된 팀 성과 데이터 또는 null
   */
  static loadCachedTeamPerformance(
    yearMonth: string,
    budgetData: BudgetData
  ): {
    teamPerformanceData: Record<string, TeamPerformanceData>;
    totalActualSales: number;
    teamTargetSales: Record<string, number>;
    calculatedAt: string;
  } | null {
    try {
      // 안전한 null 체크
      if (!budgetData || typeof budgetData !== 'object') {
        logger.warn(`[TeamPerformanceService] ${yearMonth} budgetData가 유효하지 않음`);
        return null;
      }

      const monthlyData = budgetData.monthlyData?.[yearMonth];
      
      if (monthlyData?.teamPerformanceData && 
          typeof monthlyData.teamPerformanceData === 'object' &&
          monthlyData.teamPerformanceData !== null &&
          monthlyData.totalActualSales !== undefined) {
        
        // 안전한 Object.keys 호출 - null/undefined 에러 방지
        let performanceDataKeys: string[] = [];
        try {
          performanceDataKeys = Object.keys(monthlyData.teamPerformanceData);
        } catch (error) {
          logger.error(`[TeamPerformanceService] ${yearMonth} Object.keys 에러`, error instanceof Error ? error : undefined);
          return null;
        }

        if (performanceDataKeys.length === 0) {
          logger.warn(`[TeamPerformanceService] ${yearMonth} 빈 팀 성과 데이터`);
          return null;
        }

        logger.debug(`[TeamPerformanceService] ${yearMonth} 캐시된 팀 성과 데이터 로드됨`);
        return {
          teamPerformanceData: monthlyData.teamPerformanceData,
          totalActualSales: typeof monthlyData.totalActualSales === 'number' ? monthlyData.totalActualSales : 0,
          teamTargetSales: monthlyData.teamTargetSales && typeof monthlyData.teamTargetSales === 'object' 
                          ? monthlyData.teamTargetSales : {},
          calculatedAt: monthlyData.calculatedAt || ''
        };
      }
      
      return null;
    } catch (error) {
      logger.error(`[TeamPerformanceService] ${yearMonth} 캐시된 데이터 로드 중 오류`, error instanceof Error ? error : undefined);
      return null;
    }
  }
  
  /**
   * 팀 성과 데이터를 월별 데이터에 저장합니다 (Firebase 포함)
   * @param yearMonth 저장할 년월
   * @param teamPerformanceData 팀 성과 데이터
   * @param totalActualSales 총 실제 매출
   * @param teamTargetSales 팀별 목표 매출
   * @param budgetData 현재 예산 데이터
   * @param setBudgetData 예산 데이터 설정 함수
   * @param actualSalesResult (선택) 오귀속 의심 게이트 판정용 실제매출 계산 결과.
   *   전달 시 진료의사 매핑 부재 창의 '미지정' 귀속 결과가 teamPerformanceData·totalActualSales로
   *   영속되는 것을 이 함수(단일 초크포인트)에서 차단한다. 미전달 레거시 호출자는 상위 게이트를 유지한다.
   */
  static async saveTeamPerformanceData(
    yearMonth: string,
    teamPerformanceData: Record<string, TeamPerformanceData>,
    totalActualSales: number,
    teamTargetSales: Record<string, number>,
    budgetData: BudgetData,
    setBudgetData: (data: BudgetData | ((prev: BudgetData) => BudgetData)) => void,
    actualSalesResult?: ActualSalesResult
  ): Promise<{ skipped: boolean }> {
    // 반환값: { skipped: true } = 오귀속 의심 게이트로 저장을 건너뜀(호출부가 '완료'로 오인하지 않게 신호),
    //          { skipped: false } = 정상 저장 완료. 반환값을 무시하는 레거시 호출자는 하위호환.
    try {
      // 안전한 null 체크
      if (!budgetData || typeof budgetData !== 'object') {
        throw new Error(`${yearMonth} budgetData가 유효하지 않습니다`);
      }

      if (!teamPerformanceData || typeof teamPerformanceData !== 'object') {
        throw new Error(`${yearMonth} teamPerformanceData가 유효하지 않습니다`);
      }

      if (typeof totalActualSales !== 'number' || isNaN(totalActualSales)) {
        logger.warn(`[TeamPerformanceService] ${yearMonth} totalActualSales가 유효하지 않음, 0으로 설정`);
        totalActualSales = 0;
      }

      // 🛡️ 오귀속 의심 게이트 (단일 초크포인트): 진료의사 매핑 부재 창에서 계산된 '미지정' 귀속
      // 결과는 teamPerformanceData·totalActualSales의 영속(로컬 상태·Firebase)을 금지한다.
      // 화면 표시는 호출자의 teamActualSales 실시간값으로 유지되며, 매핑 복구 후 다시 저장하면
      // 정상 반영된다. 판정 정본은 ActualSalesService.isSuspectMisattribution (refresh·급여 저장
      // 경로와 동일 규칙). 캐시 경유 결과는 플래그가 없으므로(undefined) ?? 로 재판정한다 —
      // 플래그 부재를 '의심 없음'으로 오독하면 캐시 경로가 게이트를 우회한다.
      // actualSalesResult 미전달 레거시 호출자(예: refreshService)는 자체 상위 게이트를 유지한다.
      if (actualSalesResult) {
        const suspectMisattribution = actualSalesResult.suspectMisattribution
          ?? ActualSalesService.isSuspectMisattribution(actualSalesResult, budgetData, yearMonth);
        if (suspectMisattribution) {
          logger.warn(
            `[TeamPerformanceService] ${yearMonth} 진료의사 매핑 부재 상태에서 '미지정' 귀속 매출이 감지되어 ` +
            `팀 성과 데이터 저장을 건너뜁니다 (화면 표시만 유지). 매핑 복구 후 다시 저장하세요.`
          );
          return { skipped: true };
        }
      }

      const calculatedAt = new Date().toISOString();
      const dataVersion = 'v1.0';

      // 로컬 상태 업데이트 — 함수형 패치로 최신 상태(prev) 기준, 이 경로가 소유한
      // 해당 월의 teamPerformanceData/totalActualSales/calculatedAt만 갱신
      // (refresh 시작 시점의 stale budgetData 스냅샷으로 상태 전체를 교체하던 문제 방지)
      if (typeof setBudgetData === 'function') {
        setBudgetData((prev: BudgetData) => {
          const prevMonthlyData: Record<string, any> =
            prev?.monthlyData && typeof prev.monthlyData === 'object' ? prev.monthlyData : {};
          const prevMonthData: any =
            prevMonthlyData[yearMonth] && typeof prevMonthlyData[yearMonth] === 'object'
              ? prevMonthlyData[yearMonth] : {};

          return {
            ...prev,
            monthlyData: {
              ...prevMonthlyData,
              [yearMonth]: {
                ...prevMonthData,
                teamPerformanceData,
                totalActualSales,
                // teamTargetSales는 저장하지 않음 - 급여 관리에서만 관리
                calculatedAt,
                dataVersion
              }
            }
          };
        });
        logger.debug(`[TeamPerformanceService] ${yearMonth} 로컬 상태 업데이트 완료`);
      } else {
        logger.warn(`[TeamPerformanceService] ${yearMonth} setBudgetData가 유효한 함수가 아님`);
      }

      try {
        // Firebase에 per-month 저장: 팀맵(teamPerformanceData)만 통째 교체(옛 팀 키 제거),
        // 스칼라는 mergePatch로 병합 (replaceFields는 맵 전용 계약)
        await saveBudgetMonthReplace(yearMonth, {
          totalActualSales,
          calculatedAt,
          dataVersion
        }, {
          teamPerformanceData
        });
        logger.debug(`[TeamPerformanceService] ${yearMonth} 팀 성과 데이터 Firebase 저장 완료`);
      } catch (error) {
        logger.error(`[TeamPerformanceService] ${yearMonth} Firebase 저장 실패`, error instanceof Error ? error : undefined);
        // Firebase 저장 실패해도 로컬 상태는 유지
        throw error; // Firebase 저장 실패를 상위로 전파
      }

      return { skipped: false };
    } catch (error) {
      logger.error(`[TeamPerformanceService] ${yearMonth} 팀 성과 데이터 저장 중 오류`, error instanceof Error ? error : undefined);
      throw error;
    }
  }
} 